import React from 'react';
import clsx from 'clsx';
import styles from './MetricCard.module.css';

export interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  variant?: 'blue' | 'orange' | 'green' | 'purple' | 'red' | 'yellow';
  loading?: boolean;
  className?: string;
  /** Makes the whole card a button that navigates or drills down. */
  onClick?: () => void;
  /** Extra content under the subtitle, e.g. breakdown badges. */
  children?: React.ReactNode;
  /** Stable hook for tests that need to find a specific card. */
  dataTestId?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  icon,
  label,
  value,
  subtitle,
  variant = 'blue',
  loading = false,
  className,
  onClick,
  children,
  dataTestId
}) => {
  const variantClass = {
    blue: styles.cardBlue,
    orange: styles.cardOrange,
    green: styles.cardGreen,
    purple: styles.cardPurple,
    red: styles.cardRed,
    yellow: styles.cardYellow
  }[variant];

  if (loading) {
    return (
      <div className={clsx(styles.card, styles.loading, variantClass, className)}>
        <div className={styles.skeleton}>
          <div className={styles.skeletonIcon}></div>
          <div className={styles.skeletonContent}>
            <div className={styles.skeletonLabel}></div>
            <div className={styles.skeletonValue}></div>
            {subtitle && <div className={styles.skeletonSubtitle}></div>}
          </div>
        </div>
      </div>
    );
  }

  const body = (
    <div className={styles.content}>
      <div className={styles.icon}>{icon}</div>
      <div className={styles.details}>
        <div className={styles.label}>{label}</div>
        <div className={styles.value}>{value}</div>
        {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        {children}
      </div>
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={clsx(styles.card, styles.clickable, variantClass, className)}
        onClick={onClick}
        data-test-id={dataTestId}
      >
        {body}
      </button>
    );
  }

  return (
    <div className={clsx(styles.card, variantClass, className)} data-test-id={dataTestId}>
      {body}
    </div>
  );
};

export default MetricCard;
